/**
 * DeletionRequestsModal — the owner's review queue for account deletions.
 *
 * Admins and users cannot delete accounts; they file a request that lands
 * here. Approving deletes the account for real (and, for a parent, cancels
 * their children's enrollments and stops billing), so approval asks for a
 * confirmation. Rejecting leaves the account untouched.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Check, Loader2, Trash2, X } from "lucide-react";
import toast from "react-hot-toast";
import accountDeletionService from "../../api/services/accountDeletion.service";
import ConfirmDialog from "./ConfirmDialog";
import { formatDateTime } from "../../utils/format";

const ROLE_COLORS = {
  owner: "bg-purple-100 text-purple-800",
  admin: "bg-blue-100 text-blue-800",
  coach: "bg-green-100 text-green-800",
  parent: "bg-gray-100 text-gray-800",
};

const STATUS_COLORS = {
  pending: "bg-warning-main/10 text-warning-dark",
  approved: "bg-error-light/10 text-error-main",
  rejected: "bg-neutral-lightest text-text-muted",
};

function fullName(u) {
  if (!u) return "—";
  const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  return name || u.email;
}

export default function DeletionRequestsModal({ isOpen, onClose, onChanged }) {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [notes, setNotes] = useState({});
  const [confirmApprove, setConfirmApprove] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await accountDeletionService.list(tab);
      setItems(data.items || []);
    } catch (error) {
      toast.error(error.message || "Failed to load deletion requests");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const finish = (message) => {
    toast.success(message);
    load();
    onChanged?.();
  };

  const handleApprove = async (request) => {
    setBusyId(request.id);
    try {
      await accountDeletionService.approve(request.id, notes[request.id]);
      setConfirmApprove(null);
      finish(`${fullName(request.user)}'s account has been deleted`);
    } catch (error) {
      toast.error(error.message || "Failed to approve request");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (request) => {
    setBusyId(request.id);
    try {
      await accountDeletionService.reject(request.id, notes[request.id]);
      finish("Request rejected. The account stays active.");
    } catch (error) {
      toast.error(error.message || "Failed to reject request");
    } finally {
      setBusyId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        className="bg-white rounded-[12px] shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-border-light"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 pb-4 border-b border-border-light">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-error-light/10 rounded-full flex items-center justify-center shrink-0">
              <Trash2 className="w-6 h-6 text-error-main" />
            </div>
            <div>
              <h3 className="text-lg font-bold font-manrope text-heading-dark">
                Account deletion requests
              </h3>
              <p className="text-sm font-manrope text-text-muted">
                Only you can delete accounts. Approving deletes the account now.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <X className="w-5 h-5 text-text-muted" />
          </button>
        </div>

        <div className="px-6 pt-4 flex gap-2">
          {["pending", "approved", "rejected"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold font-manrope capitalize transition ${
                tab === t
                  ? "bg-heading-dark text-white"
                  : "bg-neutral-lightest text-text-muted hover:bg-gray-200"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-text-muted">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm font-manrope text-text-muted text-center py-12">
              No {tab} deletion requests.
            </p>
          ) : (
            <ul className="space-y-3">
              {items.map((r) => {
                const isPending = r.status === "pending";
                const busy = busyId === r.id;
                return (
                  <li
                    key={r.id}
                    className="border border-border-light rounded-[10px] p-4 font-manrope"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-heading-dark">
                            {fullName(r.user)}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${
                              ROLE_COLORS[r.user?.role] || ROLE_COLORS.parent
                            }`}
                          >
                            {r.user?.role}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${
                              STATUS_COLORS[r.status] || ""
                            }`}
                          >
                            {r.status}
                          </span>
                        </div>
                        <p className="text-xs text-text-muted mt-0.5 break-all">{r.user?.email}</p>
                        <p className="text-xs text-text-muted mt-1">
                          Requested by{" "}
                          <span className="font-medium text-text-primary">
                            {r.requested_by?.id === r.user?.id ? "the user themselves" : fullName(r.requested_by)}
                          </span>{" "}
                          on {formatDateTime(r.created_at)}
                        </p>
                        {r.reason && (
                          <p className="text-sm text-text-primary mt-2">
                            <span className="text-text-muted">Reason:</span> {r.reason}
                          </p>
                        )}
                        {!isPending && (
                          <p className="text-xs text-text-muted mt-2">
                            {r.status === "approved" ? "Approved" : "Rejected"} by{" "}
                            {fullName(r.reviewed_by)}
                            {r.reviewed_at ? ` on ${formatDateTime(r.reviewed_at)}` : ""}
                            {r.review_note ? ` — ${r.review_note}` : ""}
                          </p>
                        )}
                      </div>
                    </div>

                    {isPending && (
                      <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
                        <input
                          type="text"
                          value={notes[r.id] || ""}
                          onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                          placeholder="Note (optional)"
                          className="flex-1 px-3 py-2 text-sm border border-border-light rounded-[8px] focus:outline-none focus:ring-2 focus:ring-btn-gold"
                          disabled={busy}
                        />
                        <div className="flex gap-2 shrink-0">
                          <button
                            onClick={() => handleReject(r)}
                            disabled={busy}
                            className="px-3 py-2 text-sm font-semibold text-text-primary border border-border-light rounded-[8px] hover:bg-neutral-lightest transition disabled:opacity-50 flex items-center gap-1"
                          >
                            <X className="w-4 h-4" /> Reject
                          </button>
                          <button
                            onClick={() => setConfirmApprove(r)}
                            disabled={busy}
                            className="px-3 py-2 text-sm font-semibold text-white bg-error-main hover:bg-error-dark rounded-[8px] transition disabled:opacity-50 flex items-center gap-1"
                          >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            Approve &amp; delete
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!confirmApprove}
        onClose={() => setConfirmApprove(null)}
        onConfirm={() => confirmApprove && handleApprove(confirmApprove)}
        title="Delete this account?"
        message={
          confirmApprove
            ? `${fullName(confirmApprove.user)} (${confirmApprove.user?.email}) will be deactivated immediately.${
                confirmApprove.user?.role === "parent"
                  ? " Their children's enrollments will be cancelled and billing stopped."
                  : ""
              }`
            : ""
        }
        confirmLabel="Delete account"
        variant="danger"
        isLoading={!!busyId}
      />
    </div>
  );
}
